import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { shouldReportBoardError } from "../boardFailures";
import { boardDefinitionTitle, type CreateBoardRequest } from "../BoardRecord";

import { cacheCreatedBoard } from "./refreshBoardQueries";

/**
 * Create a board. The server mints its id and returns the stored record, which is
 * the only thing that says the board exists, so a caller navigates to the new board
 * only once this resolves.
 */
export function useCreateBoard() {
  const { boardService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateBoardRequest) => {
      if (currentOrganization === null) {
        throw new Error("CurrentOrganization is not set");
      }
      // The platform refuses a create whose name differs from the definition's
      // title, so a definition with a title takes the requested name.
      const definition = request.board_definition;
      const submitted =
        boardDefinitionTitle(definition) === null
          ? request
          : {
              ...request,
              board_definition: { ...definition, title: request.name },
            };
      return boardService.createBoard(submitted, currentOrganization.org_id);
    },
    onSuccess: (record) => cacheCreatedBoard(queryClient, record),
    onError: (error) => {
      if (shouldReportBoardError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
